/**
 * Staff order — HR's tab (admin / hr_admin).
 *
 * Staff order Eduvos-branded apparel against a personal voucher. HR sets up
 * an order round (name, closing date, the words staff read), loads the
 * vouchers, tries it with testers, opens ordering, emails everyone, chases
 * the people who have not ordered and downloads the orders for the supplier.
 *
 * With no order round yet the tab is the create form. Otherwise it shows one
 * round at a time (the open one first), with a picker when there are more.
 * Backend: `/api/v1/admin/staff-orders/...` (see staffOrderService).
 */
import { useCallback, useEffect, useState } from 'react'
import { Loader2, Plus, RefreshCw, ShoppingBag } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { errorDetail, formatSastShort } from '@/lib/staffOrder'
import {
  staffOrderService,
  type StaffOrderRoundDetail,
  type StaffOrderRoundSummary,
} from '@/services/api/staffOrderService'

import { CreateRoundForm } from './staff-order/CreateRoundForm'
import { EmailActions } from './staff-order/EmailActions'
import { ItemsTable } from './staff-order/ItemsTable'
import { RoundNumbers } from './staff-order/RoundNumbers'
import { RoundSettings } from './staff-order/RoundSettings'
import { VoucherHolders } from './staff-order/VoucherHolders'
import { STATUS_BADGE, pickDefaultRound } from './staff-order/helpers'

export default function StaffOrderAdminPage() {
  const [rounds, setRounds] = useState<StaffOrderRoundSummary[] | null>(null)
  const [roundsError, setRoundsError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<StaffOrderRoundDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)

  const loadRounds = useCallback(async () => {
    setRoundsError(null)
    try {
      const list = await staffOrderService.listRounds()
      setRounds(list)
      setSelectedId((prev) => (prev && list.some((r) => r.id === prev) ? prev : pickDefaultRound(list)))
    } catch (err) {
      setRoundsError(errorDetail(err, 'The staff order could not be loaded. Please try again.'))
    }
  }, [])

  useEffect(() => {
    void loadRounds()
  }, [loadRounds])

  /** Keep the picker in step with a round the server just returned. */
  const applyDetail = useCallback((next: StaffOrderRoundDetail) => {
    setDetail(next)
    setDetailError(null)
    setRounds((prev) => {
      if (!prev) return [next]
      return prev.some((r) => r.id === next.id) ? prev.map((r) => (r.id === next.id ? next : r)) : [next, ...prev]
    })
  }, [])

  useEffect(() => {
    if (!selectedId) {
      setDetail(null)
      return
    }
    let cancelled = false
    // Another round chosen: do not leave the last one on screen while this one loads.
    setDetail((prev) => (prev && prev.id === selectedId ? prev : null))
    setDetailLoading(true)
    setDetailError(null)
    staffOrderService
      .getRound(selectedId)
      .then((d) => {
        if (!cancelled) applyDetail(d)
      })
      .catch((err) => {
        if (!cancelled) setDetailError(errorDetail(err, 'This order round could not be loaded. Please try again.'))
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedId, applyDetail])

  /** Reload the round on screen without blanking it (counts, sending progress). */
  const refresh = useCallback(async () => {
    if (!selectedId) return
    setRefreshing(true)
    try {
      applyDetail(await staffOrderService.getRound(selectedId))
    } catch (err) {
      setDetailError(errorDetail(err, 'This order round could not be refreshed. Please try again.'))
    } finally {
      setRefreshing(false)
    }
  }, [selectedId, applyDetail])

  const created = (next: StaffOrderRoundDetail) => {
    setCreateOpen(false)
    applyDetail(next)
    setSelectedId(next.id)
  }

  // ------------------------------------------------------------- render

  const title = (
    <div>
      <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
        <ShoppingBag className="h-6 w-6 text-heritage-blue" />
        Staff order
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Staff apparel ordered against a personal voucher: set up the order round, load the vouchers,
        email staff and download the orders.
      </p>
    </div>
  )

  if (rounds === null) {
    return (
      <div className="space-y-6">
        {title}
        {roundsError ? (
          <div className="space-y-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            <p>{roundsError}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => void loadRounds()}>
              <RefreshCw className="h-4 w-4" />
              Try again
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading the staff order…
          </div>
        )}
      </div>
    )
  }

  if (rounds.length === 0) {
    return (
      <div className="space-y-6">
        {title}
        <Card className="max-w-2xl">
          <CardContent className="space-y-4 p-4 sm:p-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Create order round</h3>
              <p className="text-sm text-gray-500">There is no order round yet. Start one here.</p>
            </div>
            <CreateRoundForm onCreated={created} />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        {title}
        <div className="flex flex-wrap items-center gap-2">
          {rounds.length > 1 && (
            <select
              aria-label="Order round"
              className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800"
              value={selectedId ?? ''}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {rounds.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} · {STATUS_BADGE[r.status].label}
                  {r.created_at ? ` · ${formatSastShort(r.created_at).split(',')[0]}` : ''}
                </option>
              ))}
            </select>
          )}
          <Button type="button" variant="outline" size="sm" onClick={() => void refresh()} disabled={refreshing || !detail}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            New order round
          </Button>
        </div>
      </div>

      {detailError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {detailError}
        </div>
      )}

      {!detail ? (
        detailLoading && (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading the order round…
          </div>
        )
      ) : (
        <div className="space-y-6">
          <RoundSettings detail={detail} onUpdated={applyDetail} />
          <RoundNumbers detail={detail} />
          <EmailActions detail={detail} onRefresh={refresh} refreshing={refreshing} />
          <VoucherHolders detail={detail} onChanged={() => void refresh()} />
          <ItemsTable roundId={detail.id} items={detail.items} onUpdated={applyDetail} />
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>New order round</DialogTitle>
            <DialogDescription>
              It starts as a draft: only testers see it until you open ordering, and nobody is
              emailed until you say so.
            </DialogDescription>
          </DialogHeader>
          <CreateRoundForm onCreated={created} onCancel={() => setCreateOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  )
}
